import { useMemo } from "react";
import _ from "lodash";
import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { COLOR_PRIMARY } from "../../../constants/theme";
import { useFacebookCore } from "../../../cores/facebook";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useGoogleCore } from "../../../cores/google";
import { useInstagramCore } from "../../../cores/instagram";
import { useLinkedInBusinessCore } from "../../../cores/linkedInBusiness";
import { useTiktokCore } from "../../../cores/tiktok";
import { isYoutubeConnected, useYoutubeCore } from "../../../cores/youtube";
import { isYoutubePostingEnabled } from "../../../cores/config/featureFlags";
import { useJumpsellerCore } from "../../../cores/jumpseller";
import { useMeCore } from "../../../cores/me";
import { useMercadolibreCore } from "../../../cores/mercadolibre";
import { useRouterCore } from "../../../cores/router";
import { isInstagramSocialConnected } from "../../../cores/socialPost/config";
import { useWooCommerceCore } from "../../../cores/wooCommerce";
import { JumpsellerIcon } from "../social/Jumpseller/JumpsellerIcon";
import { MercadolibreIcon } from "../social/Mercadolibre/MercadolibreIcon";
import { WooCommerceIcon } from "../social/WooCommerce/WooCommerceIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import { SidebarDisabledMessages } from "./sidebarDisabledMessages";

export interface SidebarSubItem {
  activeUrl: string;
  onClick: () => void;
  label: string;
  iconName?: IconName | undefined;
  disabled?: boolean | undefined;
  iconPrefix?: IconPrefix | undefined;
  disabledMessage?: string | undefined;
  icon?: ReactNode;
}

interface Props {
  disabledMessages: SidebarDisabledMessages;
}

const SidebarSubItems = ({ disabledMessages }: Props) => {
  const {
    planNotAllowed: planNotAllowedMessage,
    syncRequired: syncNotConnectedMessage,
    comingSoon: comingSoonMessage,
    reportsNotAllowed: reportsNotAllowedMessage,
  } = disabledMessages;
  const {
    routes,
    goToSocialStreams,
    goToSocialPost,
    goToSocialAutomatedPosts,
    goToAIGeneration,
    goToTextToImage,
    goToSocialHistory,
    goToSocialContentGrid,
    goToAnalyticsSummary,
    goToSocialIntelligence,
    goToInstagramAnalytics,
    goToTiktokAnalytics,
    goToLinkedInBusinessAnalytics,
    goToYoutubeAnalytics,
    goToFacebookAnalytics,
    goToWooCommerceAnalytics,
    goToGoogleAnalytics,
    goToWebForms,
    goToFacebookForms,
    goToEditWebChat,
    goToWebChat,
    goToWooCommerceOrders,
    goToEditWhatsappChat,
    goToMercadolibreOrders,
    goToFacebookCampaigns,
    goToMercadolibreAnalytics,
    goToContacts,
    goToContactCompanies,
    goToCalendar,
    goToSocialCalendar,
    goToSocialStory,
    goToVideoGen,
    goToVideoEditor,
    goToBusinessBoard,
    goToBusinessBoardAnalytics,
    goToWembitoChat,
    goToJumpsellerOrders,
    goToTasks,
    goToEmailMarketingLists,
    goToEmailMarketingEmails,
    goToEmailMarketingCampaign,
    goToEmailMarketingTemplates,
    goToEmailMarketingReports,
    goToEmailMarketingConfig,
  } = useRouterCore();

  const intl = useIntl();
  const { companyPlan, socialSettings, companyRole, isAdmin, isReseller } =
    useMeCore();
  const canUseSocialIntelligence = Boolean(
    companyRole?.socialIntelligence ||
      companyRole?.owner ||
      companyRole?.admin ||
      isAdmin ||
      isReseller
  );
  const {
    rules: {
      allowWebChat = false,
      allowWhatsappChat = false,
      allowFacebookAdvertisements = false,
      webFormsAmount = 0,
    } = {},
  } = companyPlan! || { name: "." };

  const { pathname } = useLocation();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: facebookAdsSettings } = useFacebookAdsCore();
  const { settings: googleSettings } = useGoogleCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: tiktokSettings } = useTiktokCore();
  const { settings: youtubeSettings } = useYoutubeCore();
  const { settings: linkedInBusinessSettings } = useLinkedInBusinessCore();
  const { settings: wooSettings } = useWooCommerceCore();
  const { settings: mercadolibreSettings } = useMercadolibreCore();
  const { settings: jumpsellerSettings } = useJumpsellerCore();
  const isFacebookSync = !_.isEmpty(facebookSettings);
  const isFacebookAdsSync = !_.isEmpty(facebookAdsSettings);
  const isInstagramSync =
    isInstagramSocialConnected(socialSettings) || !_.isEmpty(instagramSettings);
  const isTikTokSync = !_.isEmpty(tiktokSettings);
  const isYoutubeSync =
    isYoutubePostingEnabled() && isYoutubeConnected(youtubeSettings);
  const isLinkedInBusinessSync = Boolean(
    socialSettings?.linkedInBusiness?.token?.length ||
      !_.isEmpty(linkedInBusinessSettings)
  );
  const isGoogleSync = !_.isEmpty(googleSettings);
  const isWooSync = !_.isEmpty(wooSettings);
  const isMercadolibreSync = !_.isEmpty(mercadolibreSettings);
  const isJumpsellerSync = !_.isEmpty(jumpsellerSettings);
  const isWebFormAllow = webFormsAmount > 0;
  const planName = companyPlan?.name ?? ".";
  const isPremiumPlan = planName.split(".")[2] === "Premium";

  const socialSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.socialStreams,
        onClick: goToSocialStreams,
        label: intl.formatMessage({ id: "app.sidebar.socialStreams" }),
        iconName: "columns",
      },
      {
        activeUrl: routes.socialPost,
        onClick: () => goToSocialPost(""),
        label: intl.formatMessage({ id: "app.sidebar.socialMediaPost" }),
        iconName: "pen-to-square",
      },
      {
        activeUrl: routes.socialAutomatedPosts,
        onClick: goToSocialAutomatedPosts,
        label: intl.formatMessage({
          id: "app.sidebar.socialMediaAutomatedPosts",
        }),
        icon: (
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            style={{ color: COLOR_PRIMARY }}
            className="item-icon"
          />
        ),
        disabled: !isPremiumPlan,
        disabledMessage: planNotAllowedMessage,
      },
      //   activeUrl: routes.socialStory,
      //   onClick: () => goToSocialStory(""),
      //   label: intl.formatMessage({ id: "app.sidebar.socialStory" }),
      //   iconName: "history",
      // },
      {
        activeUrl: routes.socialContentGrid,
        onClick: goToSocialContentGrid,
        label: intl.formatMessage({ id: "app.sidebar.socialContentGrid" }),
        iconName: "table",
      },
      {
        activeUrl: routes.socialCalendar,
        onClick: goToSocialCalendar,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaCalendar" }),
        iconName: "calendar",
      },
      {
        activeUrl: routes.socialHistory,
        onClick: goToSocialHistory,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaRecord" }),
        iconName: "history",
      },
      {
        activeUrl: routes.facebookCampaigns,
        onClick: goToFacebookCampaigns,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaMetaAds" }),
        iconName: "meta",
        iconPrefix: "fab",
        disabled:
          !allowFacebookAdvertisements || !isFacebookAdsSync,
        disabledMessage: !allowFacebookAdvertisements
          ? planNotAllowedMessage
          : !isFacebookAdsSync
            ? syncNotConnectedMessage
            : undefined,
      },
    ];
  }, [
    routes.socialStreams,
    routes.socialPost,
    routes.socialAutomatedPosts,
    routes.socialStory,
    routes.socialContentGrid,
    routes.socialCalendar,
    routes.socialHistory,
    routes.facebookCampaigns,
    allowFacebookAdvertisements,
    isFacebookAdsSync,
    intl,
    goToSocialStreams,
    goToSocialHistory,
    goToSocialContentGrid,
    goToSocialCalendar,
    goToSocialStory,
    goToSocialPost,
    goToSocialAutomatedPosts,
    goToFacebookCampaigns,
    isPremiumPlan,
    planNotAllowedMessage,
    syncNotConnectedMessage,
  ]);

  // IA: Video Gen + Editor de imágenes (dentro del mismo menú)
  const iaSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.aiTextToImage,
        onClick: goToTextToImage,
        label: intl.formatMessage({ id: "app.sidebar.textToImage" }),
        iconName: "image",
        disabled: !isPremiumPlan,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.aiGeneration,
        onClick: goToAIGeneration,
        label: intl.formatMessage({ id: "app.sidebar.aiEditorImages" }),
        iconName: "magic",
        iconPrefix: "fas",
        disabled: !isPremiumPlan,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.videoGen,
        onClick: goToVideoGen,
        label: `${intl.formatMessage({
          id: "app.sidebar.ia.videoGen",
        })} (${intl.formatMessage({ id: "app.sidebar.comingSoon" })})`,
        iconName: "video",
        disabled: true,
        disabledMessage: comingSoonMessage,
      },
      {
        activeUrl: routes.videoEditor,
        onClick: goToVideoEditor,
        label: `${intl.formatMessage({
          id: "app.sidebar.ia.videoEditor",
        })} (${intl.formatMessage({ id: "app.sidebar.comingSoon" })})`,
        iconName: "film",
        disabled: true,
        disabledMessage: comingSoonMessage,
      },
    ];
  }, [
    routes.videoGen,
    routes.videoEditor,
    routes.aiGeneration,
    routes.aiTextToImage,
    intl,
    goToVideoGen,
    goToVideoEditor,
    goToAIGeneration,
    goToTextToImage,
    isPremiumPlan,
    planNotAllowedMessage,
    comingSoonMessage,
  ]);

  const advertisementsSubItems: SidebarSubItem[] = useMemo(() => [], []);

  const formsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.webForms,
        onClick: goToWebForms,
        label: intl.formatMessage({ id: "app.sidebar.forms.Web" }),
        iconName: "wpforms",
        iconPrefix: "fab",
        disabled: !isWebFormAllow,
        disabledMessage: !isWebFormAllow ? planNotAllowedMessage : undefined,
      },
      {
        activeUrl: routes.facebookForm,
        onClick: goToFacebookForms,
        label: intl.formatMessage({ id: "app.sidebar.forms.Facebook" }),
        iconName: "facebook",
        iconPrefix: "fab",
        disabled: !isFacebookSync,
        disabledMessage: !isFacebookSync ? syncNotConnectedMessage : undefined,
      },
    ];
  }, [
    routes.webForms,
    routes.facebookForm,
    isWebFormAllow,
    isFacebookSync,
    intl,
    goToFacebookForms,
    goToWebForms,
    planNotAllowedMessage,
    syncNotConnectedMessage,
  ]);

  const chatSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.webChat,
        onClick: goToWebChat,
        label: intl.formatMessage({ id: "app.sidebar.chat.conversations" }),
        iconName: "comments",
        disabled: !allowWebChat,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.editWebChat,
        onClick: goToEditWebChat,
        label: intl.formatMessage({ id: "app.sidebar.chat.WebChatConfig" }),
        iconName: "cog",
        disabled: !allowWebChat,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.editWhatsappChat,
        onClick: goToEditWhatsappChat,
        label: intl.formatMessage({
          id: "app.sidebar.chat.whatsappConfig",
        }),
        iconPrefix: "fab",
        iconName: "whatsapp",
        disabled: !allowWhatsappChat,
        disabledMessage: planNotAllowedMessage,
      },
    ];
  }, [
    routes.webChat,
    routes.editWebChat,
    routes.editWhatsappChat,
    allowWebChat,
    allowWhatsappChat,
    planNotAllowedMessage,
    intl,
    goToWebChat,
    goToEditWhatsappChat,
    goToEditWebChat
  ]);

  const onlineSellsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.wooCommerceOrders,
        onClick: goToWooCommerceOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.WooCommerce",
        }),
        iconName: "store",
        disabled: !isWooSync,
        disabledMessage: !isWooSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.mercadolibreOrders,
        onClick: goToMercadolibreOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.Mercadolibre",
        }),
        iconName: "store",
        disabled: !isMercadolibreSync,
        disabledMessage: !isMercadolibreSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.jumpsellerOrders,
        onClick: goToJumpsellerOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.Jumpseller",
        }),
        iconName: "store",
        disabled: !isJumpsellerSync,
        disabledMessage: !isJumpsellerSync ? syncNotConnectedMessage : undefined,
      },
    ];
  }, [
    routes.wooCommerceOrders,
    routes.jumpsellerOrders,
    routes.mercadolibreOrders,
    intl,
    isJumpsellerSync,
    isMercadolibreSync,
    isWooSync,
    pathname,
    goToJumpsellerOrders,
    goToMercadolibreOrders,
    goToWooCommerceOrders,
    syncNotConnectedMessage,
  ]);

  const crmSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        onClick: goToTasks,
        label: intl.formatMessage({ id: "app.sidebar.tasks" }),
        iconName: "tasks",
        activeUrl: routes.tasks,
      },
      {
        activeUrl: routes.calendar,
        onClick: goToCalendar,
        label: intl.formatMessage({ id: "app.sidebar.meetings" }),
        iconName: "calendar",
      },
      {
        onClick: goToBusinessBoard,
        label: intl.formatMessage({ id: "app.sidebar.crm.business" }),
        iconName: "briefcase",
        activeUrl: routes.businessBoard,
      },
      {
        onClick: goToBusinessBoardAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.crm.metrics" }),
        iconName: "chart-bar",
        activeUrl: routes.businessBoardAnalytics,
      },
      {
        onClick: goToWembitoChat,
        label: intl.formatMessage({ id: "app.sidebar.wembito" }),
        activeUrl: routes.wembitoChat,
        icon: (
          <img
            src="/assets/wembito-head.png"
            alt=""
            className="wembito-sidebar-icon"
          />
        ),
      },
    ];
  }, [
    routes.businessBoardAnalytics,
    routes.businessBoard,
    routes.calendar,
    routes.tasks,
    routes.wembitoChat,
    intl,
    goToTasks,
    goToCalendar,
    goToBusinessBoard,
    goToBusinessBoardAnalytics,
    goToWembitoChat,
  ]);

  const databaseSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.contacts,
        onClick: goToContacts,
        label: intl.formatMessage({ id: "app.sidebar.contacts" }),
        iconName: "address-book",
      },
      {
        activeUrl: routes.contactCompanies,
        onClick: goToContactCompanies,
        label: intl.formatMessage({ id: "app.sidebar.contact.companies" }),
        iconName: "building",
      },
    ];
  }, [routes.contacts, routes.contactCompanies, intl, goToContactCompanies, goToContacts]);

  const emailMarketingSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.emailMarketingLists,
        onClick: goToEmailMarketingLists,
        label: intl.formatMessage({ id: "app.sidebar.emailMarketing.lists" }),
        iconName: "list",
      },
      {
        activeUrl: routes.emailMarketingEmails,
        onClick: goToEmailMarketingEmails,
        label: intl.formatMessage({ id: "app.sidebar.emailMarketing.emails" }),
        iconName: "envelope",
      },
      {
        activeUrl: routes.emailMarketingTemplates,
        onClick: goToEmailMarketingTemplates,
        label: intl.formatMessage({ id: "app.sidebar.emailMarketing.templates" }),
        iconName: "image",
      },
      {
        activeUrl: routes.emailMarketingReports,
        onClick: goToEmailMarketingReports,
        label: intl.formatMessage({ id: "app.sidebar.emailMarketing.reports" }),
        iconName: "chart-bar",
      },
      {
        activeUrl: routes.emailMarketingConfig,
        onClick: goToEmailMarketingConfig,
        label: intl.formatMessage({ id: "app.sidebar.emailMarketing.config" }),
        iconName: "cog",
      },
    ];
  }, [
    routes.emailMarketingLists,
    routes.emailMarketingEmails,
    routes.emailMarketingCampaignDetail,
    routes.emailMarketingTemplates,
    routes.emailMarketingReports,
    routes.emailMarketingConfig,
    intl,
    goToEmailMarketingLists,
    goToEmailMarketingEmails,
    goToEmailMarketingCampaign,
    goToEmailMarketingTemplates,
    goToEmailMarketingReports,
    goToEmailMarketingConfig,
  ]);

  const metricsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.analyticsSummary,
        onClick: goToAnalyticsSummary,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Summary" }),
        iconName: "clipboard-list",
      },
      ...(canUseSocialIntelligence
        ? [
            {
              activeUrl: routes.socialIntelligence,
              onClick: goToSocialIntelligence,
              label: intl.formatMessage({
                id: "app.sidebar.metrics.socialIntelligence",
              }),
              iconName: "brain" as IconName,
            },
          ]
        : []),
      {
        activeUrl: routes.facebookAnalytics,
        onClick: goToFacebookAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Facebook" }),
        iconName: "facebook",
        iconPrefix: "fab",
        disabled: !isFacebookSync,
        disabledMessage: !isFacebookSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.instagramAnalytics,
        onClick: goToInstagramAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Instagram" }),
        iconName: "instagram",
        iconPrefix: "fab",
        disabled: !isInstagramSync,
        disabledMessage: !isInstagramSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.tiktokAnalytics,
        onClick: goToTiktokAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.TikTok" }),
        iconName: "tiktok",
        iconPrefix: "fab",
        disabled: !isTikTokSync,
        disabledMessage: !isTikTokSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.linkedinBusinessAnalytics,
        onClick: goToLinkedInBusinessAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.LinkedInBusiness" }),
        iconName: "linkedin",
        iconPrefix: "fab",
        disabled: !isLinkedInBusinessSync,
        disabledMessage: !isLinkedInBusinessSync
          ? syncNotConnectedMessage
          : undefined,
      },
      {
        activeUrl: routes.youtubeAnalytics,
        onClick: goToYoutubeAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.YouTube" }),
        iconName: "youtube",
        iconPrefix: "fab",
        disabled: !isYoutubeSync,
        disabledMessage: !isYoutubeSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.googleAnalytics,
        onClick: goToGoogleAnalytics,
        label: intl.formatMessage({
          id: "app.sidebar.metrics.GoogleAnalytics",
        }),
        iconName: "google",
        iconPrefix: "fab",
        disabled: !isGoogleSync,
        disabledMessage: !isGoogleSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.wooCommerceAnalytics,
        onClick: goToWooCommerceAnalytics,
        label: intl.formatMessage({
          id: "app.sidebar.metrics.WooCommerce",
        }),
        iconName: "chart-line",
        disabled: !isWooSync,
        disabledMessage: !isWooSync ? syncNotConnectedMessage : undefined,
      },
      {
        activeUrl: routes.mercadolibreAnalytics,
        onClick: goToMercadolibreAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.MercLibre" }),
        iconName: "chart-line",
        disabled: !isMercadolibreSync,
        disabledMessage: !isMercadolibreSync ? syncNotConnectedMessage : undefined,
      },
    ];
  }, [
    routes.mercadolibreAnalytics,
    routes.wooCommerceAnalytics,
    routes.googleAnalytics,
    routes.analyticsSummary,
    routes.socialIntelligence,
    routes.facebookAnalytics,
    routes.instagramAnalytics,
    routes.tiktokAnalytics,
    routes.linkedinBusinessAnalytics,
    routes.youtubeAnalytics,
    intl,
    isMercadolibreSync,
    isGoogleSync,
    isFacebookSync,
    isInstagramSync,
    isTikTokSync,
    isLinkedInBusinessSync,
    isYoutubeSync,
    isWooSync,
    canUseSocialIntelligence,
    pathname,
    goToMercadolibreAnalytics,
    goToWooCommerceAnalytics,
    goToAnalyticsSummary,
    goToSocialIntelligence,
    goToFacebookAnalytics,
    goToGoogleAnalytics,
    goToInstagramAnalytics,
    goToTiktokAnalytics,
    goToLinkedInBusinessAnalytics,
    goToYoutubeAnalytics,
    syncNotConnectedMessage,
  ]);

  return {
    socialSubItems,
    iaSubItems,
    advertisementsSubItems,
    formsSubItems,
    chatSubItems,
    onlineSellsSubItems,
    crmSubItems,
    databaseSubItems,
    emailMarketingSubItems,
    metricsSubItems,
  };
};

export default SidebarSubItems;
